import { Auth } from 'aws-amplify'

import { CognitoUser } from '@aws-amplify/auth'
import { cmsRoutes } from 'routes/cmsRoutes'
import apolloClient from 'configs/apolloClient'

type SignInParameters = {
  email: string
  password: string
}

export const MFA_TYPES = {
  SMS: 'SMS_MFA',
  TOTP: 'SOFTWARE_TOKEN_MFA',
} as const

export type MFAType = (typeof MFA_TYPES)[keyof typeof MFA_TYPES]

export type MFAChallenge = {
  type: MFAType
  user: CognitoUser
  phoneNumber?: string
  credentials?: {
    email: string
    password: string
  }
}

export const ADMIN_GROUP = 'admin' as const
export const SUPER_ADMIN_GROUP = 'super-admin' as const
export const AI_TRAINER = 'ai-trainer' as const
export const MARKETING_GROUP = 'marketing' as const

export type Group = typeof ADMIN_GROUP | typeof SUPER_ADMIN_GROUP | typeof AI_TRAINER | typeof MARKETING_GROUP

export const ADMIN_GROUPS = [ADMIN_GROUP, SUPER_ADMIN_GROUP, AI_TRAINER, MARKETING_GROUP]

export const COGNITO_GROUPS_KEY = 'cognito:groups'

class AuthService {
  public checkIsAdmin = async (groups?: string[]) => {
    const isAdmin = groups?.length ? ADMIN_GROUPS.find((groupName) => groups.includes(groupName)) : false
    if (!isAdmin) {
      await Auth.signOut()

      throw new Error('You do not have corresponding roles')
    }

    return isAdmin
  }

  public getCurrentUser = () => Auth.currentAuthenticatedUser()

  public getUserGroups = async (user?: CognitoUser): Promise<string[]> => {
    const cognitoUser = user ? user : await this.getCurrentUser()
    return cognitoUser?.signInUserSession?.accessToken?.payload[COGNITO_GROUPS_KEY]
  }

  public getUserId = async (): Promise<string> => {
    const cognitoUser = await this.getCurrentUser()
    return cognitoUser?.attributes?.sub
  }

  public checkIsAuthenticated = async () => {
    try {
      const groups = await this.getUserGroups()
      await this.checkIsAdmin(groups)

      return true
    } catch {
      return false
    }
  }

  public signIn = async ({ email, password }: SignInParameters): Promise<MFAChallenge | void> => {
    const response = await Auth.signIn(email, password)

    // Handle response with nextStep (Amplify v6 style) or challengeName (Amplify v5 style)
    const challengeName = response?.challengeName || response?.nextStep?.signInStep
    const user = response?.user || response

    // Check if MFA is required
    if (challengeName === 'SMS_MFA' || challengeName === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
      const phoneNumber =
        response?.challengeParam?.CODE_DELIVERY_DESTINATION || response?.nextStep?.codeDeliveryDetails?.destination

      return {
        type: MFA_TYPES.SMS,
        user,
        phoneNumber,
        credentials: { email, password },
      }
    }

    if (challengeName === 'SOFTWARE_TOKEN_MFA' || challengeName === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      return {
        type: MFA_TYPES.TOTP,
        user,
        credentials: { email, password },
      }
    }

    // No MFA required, check admin rights
    await this.verifyAdminAccess(user)
  }

  public confirmMFA = async (user: CognitoUser, code: string, mfaType: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA') => {
    const confirmedUser = await Auth.confirmSignIn(user, code, mfaType)
    await this.verifyAdminAccess(confirmedUser)
    return confirmedUser
  }

  public verifyAdminAccess = async (user: CognitoUser) => {
    const groups = await this.getUserGroups(user)
    await this.checkIsAdmin(groups)
  }

  public resendMFACode = async (credentials: { email: string; password: string }): Promise<CognitoUser> => {
    // For SMS MFA, we need to re-initiate sign in to get a new code
    const cognitoUser = await Auth.signIn(credentials.email, credentials.password)
    return cognitoUser
  }

  public getToken = async () => {
    try {
      const currentSession = await Auth.currentSession()
      const token = currentSession.getAccessToken()?.getJwtToken()
      return token ?? null
    } catch (error) {
      return null
    }
  }

  public refreshToken = async () => {
    try {
      await Auth.currentSession()
    } catch (error) {
      console.error('An error occurred while trying to refresh token', error)
      this.signOut()
      throw error
    }
  }

  public signOut = async () => {
    try {
      await Promise.all([Auth.signOut(), apolloClient.clearStore()])
    } catch (error) {
      console.error('An error occurred while trying to sign out', error)
    } finally {
      window.location.assign(cmsRoutes.SIGN_IN)
    }
  }
}

export const authService = new AuthService()
